// Reusable measured-annotation system: highlight boxes, numbered pins, a
// margin rail of cards, and dashed→solid leader lines connecting them.
//
// Every box is sized from a real DOM measurement of the element named by
// `anchorId`, via getBoundingClientRect() against `pageRef` — never from
// percentage coordinates.
//
// docs/superpowers/plans/2026-08-04-ingest-orbit-visual-redesign.md — Task 6
import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';

const PAD_X = 5;
const PAD_Y = 3;
const CARD_GAP = 10;

export interface Annotation {
  id: string;
  anchorId: string;
  severity: string;
  title: string;
  body: string;
  selected?: boolean;
}

interface AnnotationLayerProps {
  pageRef: React.RefObject<HTMLDivElement>;
  annotations: Annotation[];
  onSelect: (id: string) => void;
  disabled?: boolean;
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Leader {
  id: string;
  d: string;
  severity: string;
  on: boolean | undefined;
}

export default function AnnotationLayer({ pageRef, annotations, onSelect, disabled = false }: AnnotationLayerProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const boxRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [boxRects, setBoxRects] = useState<Record<string, Rect>>({});
  const [cardTops, setCardTops] = useState<Record<string, number>>({});
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [railMinHeight, setRailMinHeight] = useState(0);

  const measure = useCallback(() => {
    const page = pageRef.current;
    if (!page) return;
    const container = page.parentElement || page;
    const pr = container.getBoundingClientRect();

    const nextBoxRects: Record<string, Rect> = {};
    annotations.forEach((a) => {
      const el = document.getElementById(a.anchorId);
      if (!el) return;
      const r = el.getBoundingClientRect();
      nextBoxRects[a.id] = {
        left: r.left - pr.left - PAD_X,
        top: r.top - pr.top - PAD_Y,
        width: r.width + PAD_X * 2,
        height: r.height + PAD_Y * 2,
      };
    });
    setBoxRects(nextBoxRects);

    if (disabled) { setCardTops({}); setLeaders([]); setRailMinHeight(0); return; }

    let cursor = 0;
    const nextTops: Record<string, number> = {};
    const nextLeaders: Leader[] = [];
    const railLeft = railRef.current ? railRef.current.getBoundingClientRect().left - pr.left : 0;
    annotations.forEach((a) => {
      const card = cardRefs.current[a.id];
      const box = nextBoxRects[a.id];
      const h = card ? card.offsetHeight : 0;
      let want = 0;
      if (box) want = box.top + box.height / 2 - h / 2;
      const top = Math.max(cursor, Math.max(0, want));
      nextTops[a.id] = top;
      cursor = top + h + CARD_GAP;
      if (box) {
        const x1 = box.left + box.width;
        const y1 = box.top + box.height / 2;
        const y2 = top + 16;
        const mid = x1 + (railLeft - x1) / 2;
        nextLeaders.push({ id: a.id, d: `M${x1} ${y1} H${mid} V${y2} H${railLeft}`, severity: a.severity, on: a.selected });
      }
    });
    setCardTops(nextTops);
    setLeaders(nextLeaders);
    setRailMinHeight(Math.max(0, cursor - CARD_GAP));
  }, [annotations, disabled, pageRef]);

  useLayoutEffect(() => {
    measure();
    const onResize = () => requestAnimationFrame(measure);
    window.addEventListener('resize', onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => requestAnimationFrame(measure));
    const t = setTimeout(() => requestAnimationFrame(measure), 350);
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure]);

  let pinNo = 0;

  return (
    <>
      {annotations.map((a) => {
        const r = boxRects[a.id];
        const style = r
          ? { left: r.left, top: r.top, width: r.width, height: r.height }
          : { left: 0, top: 0, width: 0, height: 0 };
        return (
          <div
            key={`box-${a.id}`}
            ref={(el) => { boxRefs.current[a.id] = el; }}
            data-anno-box={a.id}
            className={`ing-anno-box ing-anno-box-${a.severity}${a.selected ? ' on' : ''}`}
            style={style}
          />
        );
      })}

      {!disabled && annotations.map((a) => {
        pinNo += 1;
        const r = boxRects[a.id];
        if (!r) return null;
        return (
          <button
            key={`pin-${a.id}`}
            type="button"
            data-anno-pin={a.id}
            className={`ing-anno-pin ing-anno-pin-${a.severity}${a.selected ? ' on' : ''}`}
            style={{ left: r.left + r.width, top: r.top }}
            onClick={() => onSelect(a.id)}
            aria-label={`${pinNo}. ${a.title}`}
          >
            {pinNo}
          </button>
        );
      })}

      {!disabled && (
        <svg ref={svgRef} className="ing-anno-leaders" aria-hidden="true">
          {leaders.map((l) => (
            <path
              key={l.id}
              d={l.d}
              className={`ing-anno-leader ing-anno-leader-${l.severity}${l.on ? ' on' : ''}`}
            />
          ))}
        </svg>
      )}

      <div
        ref={railRef}
        className={`ing-anno-rail${disabled ? ' ing-anno-rail-stacked' : ''}`}
        style={disabled || !railMinHeight ? undefined : { minHeight: railMinHeight }}
      >
        {annotations.map((a, i) => (
          <div
            key={`card-${a.id}`}
            ref={(el) => { cardRefs.current[a.id] = el; }}
            data-anno-card={a.id}
            className={`ing-anno-card ing-anno-card-${a.severity}${a.selected ? ' on' : ''}`}
            style={disabled ? undefined : { top: cardTops[a.id] ?? 0 }}
            onClick={() => onSelect(a.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(a.id); } }}
          >
            <div className="ing-anno-card-t">
              <span>{i + 1}</span> · {a.severity === 'critical' ? 'Blocker' : 'Check'}
            </div>
            <div className="ing-anno-card-h">{a.title}</div>
            <div className="ing-anno-card-d">{a.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}
